import {commitAtomicFilterTempValue} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-utilities";
import {BackendFilterOperations, BackendFilterTypes, BackendFilterValueTypes, type AtomicFilter, type BackendFilter} from "~/typeDefinitions";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {Widget} from "~/components/reports/widgetSpecific/widgets";
import {WidgetType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";

function ensureAtomicFilterValueType(filter: AtomicFilter): AtomicFilter {
    const looseFilter = filter as Omit<AtomicFilter, "valueType"> & {
        valueType?: BackendFilterValueTypes;
        globalFilterId?: Uuid;
    };

    if (looseFilter.valueType === BackendFilterValueTypes.globalFilter || looseFilter.valueType === BackendFilterValueTypes.dataDriven) {
        return filter;
    }

    if (looseFilter.globalFilterId !== undefined) {
        return {
            ...looseFilter,
            valueType: BackendFilterValueTypes.globalFilter,
        } as AtomicFilter;
    }

    return {
        ...looseFilter,
        valueType: BackendFilterValueTypes.dataDriven,
    } as AtomicFilter;
}

function commitFilterForPersistence(filter: AtomicFilter): AtomicFilter {
    const committedFilter = commitAtomicFilterTempValue(ensureAtomicFilterValueType(filter));

    if (committedFilter.operation === BackendFilterOperations.in) {
        return {
            ...committedFilter,
            tempValue: typeof committedFilter.tempValue === "string" ? committedFilter.tempValue : null,
        };
    }

    if (committedFilter.operation === BackendFilterOperations.between) {
        const rangeValue = committedFilter.value ?? committedFilter.tempValue;
        return {
            ...committedFilter,
            value: rangeValue,
            tempValue: rangeValue,
        };
    }

    const resolvedValue = committedFilter.value ?? committedFilter.tempValue;
    return {
        ...committedFilter,
        value: resolvedValue,
        tempValue: resolvedValue,
    } as AtomicFilter;
}

function cleanBackendFilter(backendFilter: BackendFilter | null | undefined): BackendFilter | null {
    if (backendFilter == null) {
        return null;
    }
    return {
        ...backendFilter,
        filters: backendFilter.filters.map((filter) => {
            if (filter.filterType !== BackendFilterTypes.atomic) {
                return filter;
            }

            return commitFilterForPersistence(filter);
        }),
    };
}

export function cleanMultiBarGraphWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.multiBarGraph) {
        throw new Error("a917e2ea-63d9-42c2-be81-1b50abd79061");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,
        granularity: widget.granularity,

        // Min max dimensions and metrics
        minDimensionSelections: widget.minDimensionSelections,
        maxDimensionSelections: widget.maxDimensionSelections,
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        selectedDimensions: widget.selectedDimensions,
        chartSeriesDetails: widget.chartSeriesDetails,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        metrics: null,
        dimensions: null,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        data: null,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,
        isTitleEdited: widget.isTitleEdited,
    };
}

export function cleanStackedBarGraphWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.stackedBarGraph) {
        throw new Error("77eb3bf2-e720-435f-905a-08dacc48268e");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,
        granularity: widget.granularity,

        // Min max dimensions and metrics
        minDimensionSelections: widget.minDimensionSelections,
        maxDimensionSelections: widget.maxDimensionSelections,
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        metrics: null,
        dimensions: null,
        chartSeriesDetails: widget.chartSeriesDetails,
        selectedSlicer: widget.selectedSlicer,
        selectedDimensions: widget.selectedDimensions,
        selectedMetrics: widget.selectedMetrics,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        data: null,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,
        isTitleEdited: widget.isTitleEdited,
    };
}

export function cleanHorizontalBarGraphWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.horizontalBarGraph) {
        throw new Error("efff4c20-476f-4d92-8064-150ed689d76c");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,
        granularity: widget.granularity,

        // Min max dimensions and metrics
        minDimensionSelections: widget.minDimensionSelections,
        maxDimensionSelections: widget.maxDimensionSelections,
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        selectedDimensions: widget.selectedDimensions,
        chartSeriesDetails: widget.chartSeriesDetails,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        metrics: null,
        dimensions: null,
        data: null,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,
        isTitleEdited: widget.isTitleEdited,
    };
}

export function cleanLineGraphWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.lineGraph) {
        throw new Error("dd2fb754-9f29-4928-bf8f-e87e7ffe2674");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,
        granularity: widget.granularity,

        // Min max dimensions and metrics
        minDimensionSelections: widget.minDimensionSelections,
        maxDimensionSelections: widget.maxDimensionSelections,
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        metrics: null,
        dimensions: null,
        selectedDimensions: widget.selectedDimensions,
        selectedMetrics: widget.selectedMetrics,
        chartSeriesDetails: widget.chartSeriesDetails,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        data: null,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,
        isTitleEdited: widget.isTitleEdited,
    };
}

export function cleanAreaGraphWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.areaGraph) {
        throw new Error("fb1f67de-8d3b-4a6d-bd4a-be043cd19541");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,
        granularity: widget.granularity,

        // Min max dimensions and metrics
        minDimensionSelections: widget.minDimensionSelections,
        maxDimensionSelections: widget.maxDimensionSelections,
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        metrics: null,
        dimensions: null,
        selectedDimensions: widget.selectedDimensions,
        selectedMetrics: widget.selectedMetrics,
        chartSeriesDetails: widget.chartSeriesDetails,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        data: null,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,
        isTitleEdited: widget.isTitleEdited,
    };
}

export function cleanComboChartWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.comboChart) {
        throw new Error("831e7525-4845-4176-805e-73be3ac3c11c");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,
        granularity: widget.granularity,

        // Min max dimensions and metrics
        minDimensionSelections: widget.minDimensionSelections,
        maxDimensionSelections: widget.maxDimensionSelections,
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        metrics: null,
        dimensions: null,
        chartSeriesDetails: widget.chartSeriesDetails,
        selectedDimensions: widget.selectedDimensions,
        selectedMetrics: widget.selectedMetrics,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        data: null,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,
        isTitleEdited: widget.isTitleEdited,
    };
}

export function cleanPieChartWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.pieChart) {
        throw new Error("9489f4c0-5bcf-4539-bccc-d419a7382dd1");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,

        // Min max dimensions and metrics
        minDimensionSelections: widget.minDimensionSelections,
        maxDimensionSelections: widget.maxDimensionSelections,
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        metrics: null,
        dimensions: null,
        selectedDimensions: widget.selectedDimensions,
        selectedMetrics: widget.selectedMetrics,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        data: null,

        // Visuals
        valuesPrefix: widget.valuesPrefix,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,
    };
}

export function cleanDonutChartWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.donutChart) {
        throw new Error("414c0ed7-7ae8-48df-b264-bc0f3d1d0037");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,

        // Min max dimensions and metrics
        minDimensionSelections: widget.minDimensionSelections,
        maxDimensionSelections: widget.maxDimensionSelections,
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        metrics: null,
        dimensions: null,
        selectedDimensions: widget.selectedDimensions,
        selectedMetrics: widget.selectedMetrics,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        data: null,

        // Visuals
        valuesPrefix: widget.valuesPrefix,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,
    };
}

export function cleanTableWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.table) {
        throw new Error("9b05b7f4-aa9b-418e-9a38-3589f32641af");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,
        granularity: widget.granularity,

        // Min max dimensions and metrics
        minDimensionSelections: widget.minDimensionSelections,
        maxDimensionSelections: widget.maxDimensionSelections,
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        metrics: null,
        dimensions: null,
        selectedDimensions: widget.selectedDimensions,
        selectedMetrics: widget.selectedMetrics,
        transposeDimensionId: widget.transposeDimensionId ?? null,
        comparison: widget.comparison,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        data: null,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,

        // Visuals
        columnProperties: widget.columnProperties,
        columnsToSort: widget.columnsToSort,
    };
}

export function cleanNumberTileWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.numberTile) {
        throw new Error("c8e73091-1cef-4c9b-a383-31e66aa4a1d3");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Date and granularity related
        dateRange: widget.dateRange,
        dateRangeCanvasControl: widget.dateRangeCanvasControl,
        startDateIso: widget.startDateIso,
        endDateIso: widget.endDateIso,

        // Min max dimensions and metrics
        minMetricSelections: widget.minMetricSelections,
        maxMetricSelections: widget.maxMetricSelections,

        // Data + Filters
        datasetId: widget.datasetId,
        metrics: null,
        selectedMetrics: widget.selectedMetrics,
        backendFilter: cleanBackendFilter(widget.backendFilter),
        filters: widget.filters,
        areCanvasControlsLinked: widget.areCanvasControlsLinked,
        columnMetadata: undefined,
        columnAttributeIds: undefined,
        aggregatedValue: undefined,

        // Visuals
        decimalPlaces: widget.decimalPlaces,
        numberSystem: widget.numberSystem,
        spreadValue: widget.spreadValue,
        valuePrefix: widget.valuePrefix,
        valueSuffix: widget.valueSuffix,
        title: widget.title === undefined || widget.title === null || widget.title.trim() === "" ? null : widget.title,
        isTitleEdited: widget.isTitleEdited,
        fontSizeMultiplier: widget.fontSizeMultiplier,
    };
}

export function cleanTextWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    if (widget.type !== WidgetType.text) {
        throw new Error("84c5825f-ac29-4bac-9f82-fa7f17d25ec6");
    }

    return {
        // Generic widget stuff
        id: widget.id,
        col: widget.col,
        colSpan: widget.colSpan,
        row: widget.row,
        rowSpan: widget.rowSpan,
        type: widget.type,

        // Visuals
        bold: widget.bold,
        italic: widget.italic,
        underline: widget.underline,
        strikethrough: widget.strikethrough,
        textHorizontalAlign: widget.textHorizontalAlign,
        textVerticalAlign: widget.textVerticalAlign,
        fontSizeMultiplier: widget.fontSizeMultiplier,
        text: widget.text,
    };
}

export function genericCleanWidgetForDatabaseSync(widget: Widget): Partial<Widget> {
    return {
        ...widget,
        startDateJs: null,
        endDateJs: null,
        data: null,
        xAxisKey: null,
        xAxisTitle: null,
        yAxisTitle: null,
        metricValue: null,
        aggregatedValue: null,
        metrics: null,
        dimensions: null,
    };
}
